import { useSearchParams } from "react-router-dom";
import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { ArrowRight, CalendarIcon, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { getBundles } from "@/features/bundles/services/get-bundles";
import { Bundle } from "@/pages/bundles/types/bundle";
import { FLAG_CONFIG, DiscrepancyFlag } from "@/pages/audit/types/bundle-discrepancy";

const FLAG_OPTIONS: { value: string; label: string }[] = [
  { value: "ALL", label: "All Discrepancies" },
  ...(Object.entries(FLAG_CONFIG) as [DiscrepancyFlag, { label: string }][]).map(
    ([key, cfg]) => ({ value: key, label: cfg.label })
  ),
];

export function BundleDiscrepancyFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Local input state — only committed to URL on Enter / button click
  const [userSearchInput, setUserSearchInput] = useState(
    searchParams.get("user_search") || ""
  );

  const bundle_id = searchParams.get("bundle_id") || "ALL";
  const flag = searchParams.get("flag") || "ALL";
  const dateFrom = searchParams.get("date_from");
  const dateTo = searchParams.get("date_to");

  const { data: bundleData, isLoading: bundlesLoading } = useQuery({
    queryKey: ["bundles-all-active"],
    queryFn: () => getBundles({ page: 0, status: "ACTIVE", limit: 100 }),
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
  const bundleOptions = useMemo(
    () => [
      { value: "ALL", label: "All Bundles" },
      ...(bundleData?.bundles ?? []).map((b: Bundle) => ({ value: b.id, label: b.name })),
    ],
    [bundleData]
  );

  const form = useForm({
    defaultValues: {
      dateRange: {
        start: dateFrom ? new Date(dateFrom) : null,
        end: dateTo ? new Date(dateTo) : null,
      },
    },
  });

  const handleFilterChange = (updates: Record<string, any>) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", "0");

    Object.entries(updates).forEach(([key, value]) => {
      if (key === "dateRange") {
        if (value.start && value.end) {
          params.set("date_from", value.start.toISOString());
          const end = new Date(value.end);
          end.setHours(23, 59, 59, 999);
          params.set("date_to", end.toISOString());
        } else {
          params.delete("date_from");
          params.delete("date_to");
        }
      } else if (value && value !== "ALL") {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });

    setSearchParams(params);
  };

  const commitUserSearch = () => {
    const params = new URLSearchParams(searchParams);
    if (userSearchInput.trim()) {
      params.set("user_search", userSearchInput.trim());
    } else {
      params.delete("user_search");
    }
    params.set("page", "0");
    setSearchParams(params);
  };

  const hasActiveFilters =
    bundle_id !== "ALL" ||
    flag !== "ALL" ||
    !!dateFrom ||
    !!dateTo ||
    !!searchParams.get("user_search");

  const handleReset = () => {
    setUserSearchInput("");
    form.reset({ dateRange: { start: null, end: null } });
    const params = new URLSearchParams();
    params.set("page", "0");
    setSearchParams(params);
  };

  return (
    <Form {...form}>
      <div className="flex flex-wrap items-center gap-3">

        {/* User search — fires on Enter or button click */}
        <div className="flex items-center gap-1">
          <Input
            placeholder="Search by name or phone..."
            value={userSearchInput}
            onChange={(e) => setUserSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && commitUserSearch()}
            className="w-52 h-9 text-sm"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={commitUserSearch}
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>

        {/* Bundle select */}
        <Select
          value={bundle_id}
          onValueChange={(v) => handleFilterChange({ bundle_id: v })}
          disabled={bundlesLoading}
        >
          <SelectTrigger className="w-48 bg-white text-gray-900 border-gray-300">
            <SelectValue placeholder="All Bundles" />
          </SelectTrigger>
          <SelectContent>
            {bundleOptions.map((b) => (
              <SelectItem key={b.value} value={b.value}>
                {b.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Discrepancy flag select */}
        <Select
          value={flag}
          onValueChange={(v) => handleFilterChange({ flag: v })}
        >
          <SelectTrigger className="w-52 bg-white text-gray-900 border-gray-300">
            <SelectValue placeholder="All Discrepancies" />
          </SelectTrigger>
          <SelectContent>
            {FLAG_OPTIONS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date range — Popover + Calendar (Sessions pattern) */}
        <FormField
          control={form.control}
          name="dateRange"
          render={({ field }) => (
            <FormItem>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={`w-[200px] justify-start text-left font-normal bg-white text-gray-900 border-gray-300 hover:bg-gray-50 ${
                        !field.value.start && "text-gray-500"
                      }`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value.start && field.value.end ? (
                        <>
                          {format(field.value.start, "LLL dd, y")} –{" "}
                          {format(field.value.end, "LLL dd, y")}
                        </>
                      ) : (
                        <span>End date range</span>
                      )}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4" align="start">
                  {/* Display boxes */}
                  <div className="flex items-end gap-3 mb-4 px-4">
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1.5 block">
                        Start
                      </label>
                      <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                        {field.value.start ? (
                          format(field.value.start, "LLL dd, y")
                        ) : (
                          <span className="text-muted-foreground">Select</span>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground mb-3" />
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1.5 block">
                        End
                      </label>
                      <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                        {field.value.end ? (
                          format(field.value.end, "LLL dd, y")
                        ) : (
                          <span className="text-muted-foreground">Select</span>
                        )}
                      </div>
                    </div>
                    {(field.value.start || field.value.end) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          const cleared = { start: null, end: null };
                          field.onChange(cleared);
                          handleFilterChange({ dateRange: cleared });
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <Calendar
                    initialFocus
                    mode="range"
                    selected={{
                      from: field.value.start || undefined,
                      to: field.value.end || undefined,
                    }}
                    onSelect={(range) => {
                      const newValue = {
                        start: range?.from || null,
                        end: range?.to || null,
                      };
                      field.onChange(newValue);
                      if (range?.from && range?.to) {
                        handleFilterChange({ dateRange: newValue });
                      }
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </FormItem>
          )}
        />

        {/* Reset */}
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={handleReset}>
            <X className="h-4 w-4 mr-1" />
            Reset
          </Button>
        )}
      </div>
    </Form>
  );
}
